import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import UAParser from 'ua-parser-js';
import { ANALYTIKA_EVENT_SOURCE } from '../constants';
import { publishEvent } from '../../api/analytika';
import { AnalytikaEventsAttrType, AnalytikaExtendedType, AnalytikaPayloadType } from '../../api/dtos/analytika';
import { AppState } from '../../types/store';
import { ANALYTIKA_ROUTES_TO_SCREENS, ANALYTIKA_SCREENS } from './enums';
import { v4 as uuidv4 } from 'uuid';
import { getUserIdFromToken } from '../auth';

const setSessionParams = (): { sessionId: string; sessionStartedAt: number } => {
  const sessionId = uuidv4();
  const sessionStartedAt = new Date().getTime();
  sessionStorage.setItem('sessionId', sessionId);
  sessionStorage.setItem('sessionStartedAt', sessionStartedAt.toString());

  return { sessionId, sessionStartedAt };
};

export const getSessionParams = (): { sessionId: string; sessionStartedAt: number } => {
  const existingSessionId = sessionStorage.getItem('sessionId') || undefined;
  const existingSessionStartedAt = sessionStorage.getItem('sessionStartedAt') || undefined;
  if (!existingSessionId || !existingSessionStartedAt) {
    return setSessionParams();
  }
  return { sessionId: existingSessionId, sessionStartedAt: +existingSessionStartedAt };
};

const useAnalytika: () => AnalytikaExtendedType = () => {
  const appState = useSelector((state: AppState) => state);
  const {
    user: { userSettings, userData }
  } = appState;
  const userId = getUserIdFromToken();
  const userName = userData?.name;
  const userEmail = userData?.email;
  const userPhone = userData?.phone;
  const userCityName = userData?.city?.name;
  const language = userSettings?.defaultLanguage;
  const fireEvent = useCallback(
    (attributes: AnalytikaEventsAttrType) => {
      const { action, ...rest } = attributes;
      const { browser, os, device } = UAParser();
      const { sessionId, sessionStartedAt } = getSessionParams();
      const timeSinceSessionStart = new Date().getTime() - new Date(sessionStartedAt).getTime();

      const payload: AnalytikaPayloadType = {
        sessionAttributes: {
          sessionUuid: sessionId,
          appName: ANALYTIKA_EVENT_SOURCE,
          userId,
          browserName: browser.name,
          browserVersion: browser.version,
          osName: os.name,
          osVersion: os.version,
          deviceManufacturer: device.vendor,
          deviceType: device.type ?? 'Desktop',
          deviceModel: device.model
        },
        events: [
          {
            ...rest,
            action,
            user_name: userName,
            user_phone: userPhone,
            user_email: userEmail,
            location_name: userCityName,
            timestamp: Date.now(),
            business: 'merchant',
            merchant_id: userId,
            eventSource: ANALYTIKA_EVENT_SOURCE,
            timeSinceSessionStart,
            language
          }
        ]
      };
      publishEvent(payload);
    },
    [userData, language]
  );

  return { fireEvent, appState };
};

export const publishEventEffect = (attributes: AnalytikaEventsAttrType, appState: AppState): void => {
  const {
    user: { userSettings, userData }
  } = appState;
  const userId = getUserIdFromToken();
  const userName = userData?.name;
  const userEmail = userData?.email;
  const userPhone = userData?.phone;
  const userCityName = userData?.city?.name;
  const language = userSettings?.defaultLanguage;
  const { action, ...rest } = attributes;
  const { browser, os, device } = UAParser();
  const { sessionId, sessionStartedAt } = getSessionParams();
  const timeSinceSessionStart = new Date().getTime() - new Date(sessionStartedAt).getTime();
  const payload: AnalytikaPayloadType = {
    sessionAttributes: {
      sessionUuid: sessionId,
      appName: ANALYTIKA_EVENT_SOURCE,
      userId,
      browserName: browser.name,
      browserVersion: browser.version,
      osName: os.name,
      osVersion: os.version,
      deviceManufacturer: device.vendor,
      deviceType: device.type ?? 'Desktop',
      deviceModel: device.model
    },
    events: [
      {
        ...rest,
        action,
        user_name: userName,
        user_phone: userPhone,
        user_email: userEmail,
        location_name: userCityName,
        timestamp: Date.now(),
        business: 'merchant',
        merchant_id: userId,
        eventSource: ANALYTIKA_EVENT_SOURCE,
        timeSinceSessionStart,
        language
      }
    ]
  };
  publishEvent(payload);
};

export const getAnalytikaScreenName = (pathname: string | string[] = ''): ANALYTIKA_SCREENS => {
  const path = Array.isArray(pathname) ? pathname[0] : pathname;

  // @ts-ignore
  const baseScreenName = ANALYTIKA_ROUTES_TO_SCREENS[path];

  if (!baseScreenName && /^\/deliveries\/active\/(\d+)$/.test(path)) {
    return ANALYTIKA_SCREENS.MARKETPLACE_ACTIVE_DELIVERY_DETAILS;
  }
  if (!baseScreenName && /^\/deliveries\/past\/(\d+)$/.test(path)) {
    return ANALYTIKA_SCREENS.MARKETPLACE_COMPLETED_DELIVERY_DETAILS;
  }
  return baseScreenName || ANALYTIKA_SCREENS.NO_SCREEN;
};

export default useAnalytika;
